import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import { Reveal, RevealText } from "@/components/Reveal";

export default function Home() {
  const [info, setInfo] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/site/info").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-cream grain">
      {/* HERO */}
      <section
        data-testid={HOME.hero}
        className="relative min-h-screen pt-32 pb-20 px-6 md:px-12 lg:px-16 max-w-[1400px] mx-auto"
      >
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-2">
          </div>

          <div className="col-span-12 md:col-span-10">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="text-sm uppercase tracking-[0.22em] text-peach mb-6 md:mb-10"
            >
              Brand Strategist · Content Architect
            </motion.p>

            <h1 className="font-heading font-light text-[15vw] md:text-[11vw] leading-[0.85] tracking-[-0.04em] text-ink">
              <RevealText text="Giovanni" />
              <br />
              <span className="italic text-peach">
                <RevealText text="Gabban." delay={0.3} />
              </span>
            </h1>

            <div className="mt-16 md:mt-24 grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-7 md:col-start-6">
                <Reveal delay={0.4}>
                  <p className="font-heading text-2xl md:text-4xl leading-tight text-ink/80 italic">
                    "Tiro fuori il <span className="not-italic text-peach">perché</span> delle aziende.
                    <br />
                    Poi costruisco la comunicazione attorno a quello."
                  </p>
                </Reveal>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-10 left-6 md:left-12 lg:left-16 flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-ink/50"
        >
          <span className="w-12 h-px bg-ink/40" />
          Scroll
        </motion.div>
      </section>

      {/* MANIFESTO */}
      <section
        data-testid={HOME.manifesto}
        className="py-32 md:py-48 px-6 md:px-12 lg:px-16 max-w-[1400px] mx-auto"
      >
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-4">— 01 / Manifesto</p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <Reveal>
              <p className="font-heading text-3xl md:text-5xl leading-[1.15] text-ink tracking-tight">
                Lavoro meglio con imprese che hanno <em className="text-peach">qualcosa di vero</em> da dire e non sanno bene come dirlo. Non entro in azienda per gestire i social: entro per capire <em>perché</em> quell'azienda esiste — e poi costruisco l'architettura di contenuti che ne deriva.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/about"
                className="mt-12 inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] editorial-link"
              >
                Continua a leggere <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROJECTS INDEX */}
      <section
        data-testid={HOME.projectsSection}
        className="border-t border-line py-24 md:py-32 px-6 md:px-12 lg:px-16 max-w-[1400px] mx-auto"
      >
        <div className="flex items-end justify-between mb-16 md:mb-24">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-4">— 02 / Lavori</p>
            <h2 className="font-heading text-5xl md:text-7xl tracking-tighter">Indice progetti</h2>
          </div>
          <Link
            to="/progetti"
            className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] editorial-link"
          >
            Tutti i progetti <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="space-y-0">
          {projects.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <Link
                to={`/progetti/${p.slug}`}
                data-testid={HOME.projectCard(p.slug)}
                className="group grid grid-cols-12 gap-6 py-8 md:py-12 border-t border-line hover:bg-cream-alt/40 transition-colors px-2 md:px-4 -mx-2 md:-mx-4"
              >
                <div className="col-span-2 md:col-span-1">
                  <span className="num-index text-3xl md:text-5xl text-line-dark group-hover:text-peach transition-colors">
                    {String(p.index).padStart(2, "0")}
                  </span>
                </div>
                <div className="col-span-10 md:col-span-5">
                  <h3 className="font-heading text-3xl md:text-5xl tracking-tight leading-[1] text-ink group-hover:translate-x-3 transition-transform duration-700">
                    {p.title}
                  </h3>
                  <p className="text-xs uppercase tracking-[0.22em] text-ink/50 mt-3">
                    {p.context} — {p.year}
                  </p>
                </div>
                <div className="hidden md:block md:col-span-5">
                  <p className="font-heading italic text-xl text-ink/70 leading-snug">{p.tagline}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs uppercase tracking-widest text-ink/40">
                    {p.tags.slice(0, 3).map((t) => <span key={t}>· {t}</span>)}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-1 flex md:justify-end items-center">
                  <ArrowUpRight
                    size={28}
                    className="text-ink/30 group-hover:text-peach group-hover:rotate-45 transition-all duration-500"
                  />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 md:py-48 px-6 md:px-12 lg:px-16 max-w-[1400px] mx-auto">
        <div className="border-t border-line pt-16 md:pt-24 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8">
            <Reveal>
              <h2 className="font-heading text-5xl md:text-8xl tracking-tighter leading-[0.95]">
                Hai qualcosa<br />
                <span className="italic text-peach">di vero</span> da dire?
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-4 md:pt-8">
            <Reveal delay={0.2}>
              <p className="text-ink/70 mb-8 max-w-sm">
                Parliamo del tuo posizionamento, dei tuoi contenuti, della strategia che manca.
              </p>
              <Link
                to="/contatti"
                data-testid={HOME.ctaContact}
                className="inline-flex items-center gap-3 bg-peach text-cream rounded-full px-8 py-5 text-xs uppercase tracking-[0.22em] hover:bg-peach-hover transition-colors"
              >
                Scrivimi <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
